import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _regeneratorRuntime from "@babel/runtime/regenerator";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { useEffect, useState, useRef } from 'react';
import { useCallbackOne as useCallback, useMemoOne as useMemo } from 'use-memo-one';
import deepEqual from 'fast-deep-equal';
import { OPERATIONAL_EVENT_TYPE, UI_EVENT_TYPE } from '@atlaskit/analytics-gas-types';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { FORGE_UI_ANALYTICS_CHANNEL } from '../analytics';
import { invokeAuxEffectsMutation } from '../web-client';
import { parseExtensionId } from '../utils';
function isRenderEffect(effect) {
  return effect.type === 'render' && !!effect.aux && !!effect.state;
}
function isResultEffect(effect) {
  return effect.type === 'result' && !!effect.forgeDoc && !!effect.state;
}
export function isClientEffect(effect) {
  return isRenderEffect(effect) || isResultEffect(effect);
}
export function isArrayOfClientEffects(effects) {
  return effects.every(isClientEffect);
}
function isGQLInvokeAuxEffectsNetworkFailure(response) {
  return typeof response.success === 'undefined';
}
function isGQLInvokeAuxEffectsResponseSucceeded(response) {
  return response.success;
}
var downgradeEffect = function downgradeEffect(effect) {
  if (effect.type === 'render') {
    return {
      type: 'initialize'
    };
  } else if (effect.type === 'event') {
    return {
      type: 'event',
      handler: effect.handler,
      args: effect.args
    };
  }
};
var getRuntimeStateError = function getRuntimeStateError(response) {
  var result = response.result,
    errors = response.errors;
  if (isGQLInvokeAuxEffectsNetworkFailure(response)) {
    return 'Unexpected error occurred. Try refreshing your browser.';
  } else if (!isGQLInvokeAuxEffectsResponseSucceeded(response)) {
    var error = errors === null || errors === void 0 ? void 0 : errors[0];
    var statusCode = error === null || error === void 0 ? void 0 : error.extensions.statusCode;
    var message = error === null || error === void 0 ? void 0 : error.message;
    if (statusCode === 400 || statusCode === 500) {
      if (!(result !== null && result !== void 0 && result.effects)) {
        return message;
      } else if (!isArrayOfClientEffects(result.effects)) {
        return 'received a non-client effect';
      }
      return undefined;
    }
    return message;
  } else if (!!result && !isArrayOfClientEffects(result.effects)) {
    return 'received a non-client effect';
  }
  return undefined;
};
function useIsUnmountedRef() {
  var isUnmountedRef = useRef(false);
  useEffect(function () {
    return function () {
      isUnmountedRef.current = true;
    };
  }, []);
  return isUnmountedRef;
}
export var useWebRuntime = function useWebRuntime(_ref) {
  var apolloClient = _ref.apolloClient,
    contextIds = _ref.contextIds,
    extensionId = _ref.extensionId,
    coreData = _ref.coreData,
    entryPoint = _ref.entryPoint,
    getContextToken = _ref.getContextToken,
    resetStateOnExtensionDataChange = _ref.resetStateOnExtensionDataChange;
  var _useState = useState({
      forgeDoc: undefined,
      appTime: 0,
      error: undefined,
      loading: false
    }),
    _useState2 = _slicedToArray(_useState, 2),
    _useState2$ = _useState2[0],
    forgeDoc = _useState2$.forgeDoc,
    appTime = _useState2$.appTime,
    error = _useState2$.error,
    loading = _useState2$.loading,
    setRuntimeState = _useState2[1];
  var previousProps = useRef({
    extensionId: extensionId,
    entryPoint: entryPoint
  });
  var extensionData = useRef({});
  var appState = useRef({});
  if (!deepEqual(previousProps.current, {
    extensionId: extensionId,
    entryPoint: entryPoint
  })) {
    appState.current = {};
    previousProps.current = {
      extensionId: extensionId,
      entryPoint: entryPoint
    };
  }
  var contextToken = useRef(undefined);
  var isUnmountedRef = useIsUnmountedRef();
  var _useAnalyticsEvents = useAnalyticsEvents(),
    createAnalyticsEvent = _useAnalyticsEvents.createAnalyticsEvent;

  // TODO: remove this event once we have switched over to the new event schema
  // https://ecosystem-platform.atlassian.net/browse/EXT-2196
  useEffect(function () {
    createAnalyticsEvent({
      eventType: UI_EVENT_TYPE,
      data: {
        action: 'viewed',
        actionSubject: 'forgeUIExtension',
        actionSubjectId: 'editorMacro'
      }
    }).fire(FORGE_UI_ANALYTICS_CHANNEL);
  }, [createAnalyticsEvent]);

  /* eslint-disable */
  /* prettier-ignore */
  // Disabled to prevent eslint auto-fixing these hooks.
  // The autofix breaks usages like: useWebRuntime({ coreData: { localId, cloudId } })
  var memoedContextIds = useMemo(function () {
    return contextIds;
  }, [contextIds.sort().join()]);
  var _parseExtensionId = parseExtensionId(extensionId),
    moduleKey = _parseExtensionId.moduleKey;
  var siteUrl = window.location.origin;
  var memoedCoreData = useMemo(function () {
    return _objectSpread(_objectSpread({}, coreData), {}, {
      moduleKey: moduleKey,
      siteUrl: siteUrl
    });
  }, [coreData.localId, coreData.cloudId, moduleKey, siteUrl]);

  /* eslint-enable */
  var dispatch = useCallback( /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(effect) {
      var _contextToken$current, _effect$extensionData, mutationResult, response, result;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            performance.mark("forge-ui.fe.render-start-".concat(memoedCoreData.localId));
            _context.t0 = effect.type;
            _context.next = _context.t0 === 'event' ? 4 : _context.t0 === 'render' ? 4 : 44;
            break;
          case 4:
            if (resetStateOnExtensionDataChange) {
              if (!deepEqual(effect.extensionData, extensionData.current)) {
                appState.current = {};
              }
            }
            extensionData.current = effect.extensionData;
            setRuntimeState(function (_ref3) {
              var forgeDoc = _ref3.forgeDoc,
                appTime = _ref3.appTime,
                error = _ref3.error;
              return {
                forgeDoc: forgeDoc,
                appTime: appTime,
                error: error,
                loading: true
              };
            });
            _context.prev = 7;
            _context.t1 = apolloClient;
            _context.t2 = invokeAuxEffectsMutation;
            _context.t3 = memoedContextIds;
            _context.t4 = extensionId;
            _context.t5 = entryPoint;
            _context.t6 = appState.current;
            _context.t7 = _objectSpread(_objectSpread({}, memoedCoreData), {}, {
              extensionData: effect.extensionData
            });
            _context.t8 = effect.extensionPayload;
            if (!getContextToken) {
              _context.next = 22;
              break;
            }
            _context.next = 19;
            return getContextToken();
          case 19:
            _context.t9 = _context.sent;
            _context.next = 23;
            break;
          case 22:
            _context.t9 = (_contextToken$current = contextToken.current) === null || _contextToken$current === void 0 ? void 0 : _contextToken$current.jwt;
          case 23:
            _context.t10 = _context.t9;
            _context.t11 = [downgradeEffect(effect)];
            _context.t12 = (_effect$extensionData = effect.extensionData) === null || _effect$extensionData === void 0 ? void 0 : _effect$extensionData.config;
            _context.t13 = {
              state: _context.t6,
              context: _context.t7,
              extensionPayload: _context.t8,
              contextToken: _context.t10,
              effects: _context.t11,
              config: _context.t12
            };
            _context.t14 = {
              contextIds: _context.t3,
              extensionId: _context.t4,
              entryPoint: _context.t5,
              payload: _context.t13
            };
            _context.t15 = {
              input: _context.t14
            };
            _context.t16 = {
              mutation: _context.t2,
              variables: _context.t15
            };
            _context.next = 32;
            return _context.t1.mutate.call(_context.t1, _context.t16);
          case 32:
            mutationResult = _context.sent;
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            response = mutationResult.data.invokeAuxEffects || {};
            result = response.result;
            contextToken.current = result === null || result === void 0 ? void 0 : result.contextToken;
            setRuntimeState(function (_ref4) {
              var _result$metrics;
              var forgeDoc = _ref4.forgeDoc;
              return {
                forgeDoc: function () {
                  if (!result || !result.effects[0]) {
                    return forgeDoc;
                  }
                  var resultEffect = result.effects[0];
                  appState.current = resultEffect.state;
                  return resultEffect.aux;
                }(),
                appTime: (result === null || result === void 0 || (_result$metrics = result.metrics) === null || _result$metrics === void 0 ? void 0 : _result$metrics.appTimeMs) || 0,
                error: getRuntimeStateError(response),
                loading: false
              };
            });
            if (!isUnmountedRef.current) {
              requestAnimationFrame(function () {
                setTimeout(function () {
                  performance.mark("forge-ui.fe.render-end-".concat(memoedCoreData.localId));
                  performance.measure('forge-ui.fe.render', "forge-ui.fe.render-start-".concat(memoedCoreData.localId), "forge-ui.fe.render-end-".concat(memoedCoreData.localId));
                });
              });

              // this tracks every time an app is rendered
              createAnalyticsEvent({
                eventType: OPERATIONAL_EVENT_TYPE,
                data: {
                  action: 'rendered',
                  actionSubject: 'forge.ui.renderer',
                  attributes: {
                    target: 'useWebRuntime'
                  },
                  tags: ['forge']
                }
              }).fire(FORGE_UI_ANALYTICS_CHANNEL);
            }
            _context.next = 43;
            break;
          case 40:
            _context.prev = 40;
            _context.t17 = _context["catch"](7);
            setRuntimeState(function (_ref5) {
              var forgeDoc = _ref5.forgeDoc,
                appTime = _ref5.appTime;
              return {
                forgeDoc: forgeDoc,
                appTime: appTime,
                error: _context.t17 && _context.t17.message,
                loading: false
              };
            });
          case 43:
            return _context.abrupt("return");
          case 44:
            setRuntimeState(function (_ref6) {
              var forgeDoc = _ref6.forgeDoc,
                appTime = _ref6.appTime,
                loading = _ref6.loading;
              return {
                forgeDoc: forgeDoc,
                appTime: appTime,
                error: "Internal Error: Don't know how to handle effect ".concat(effect, "."),
                loading: loading
              };
            });
          case 45:
          case "end":
            return _context.stop();
        }
      }, _callee, null, [[7, 40]]);
    }));
    return function (_x) {
      return _ref2.apply(this, arguments);
    };
  }(), [apolloClient, memoedContextIds, extensionId, appState, memoedCoreData, isUnmountedRef, entryPoint, getContextToken, resetStateOnExtensionDataChange, createAnalyticsEvent]);
  Object.freeze(forgeDoc);
  return [dispatch, {
    forgeDoc: forgeDoc,
    appTime: appTime,
    loading: loading,
    error: error
  }];
};